import { darken, lighten } from '@mui/material';
import { createTheme, styled } from '@mui/material/styles';
import { MaterialDesignContent } from 'notistack';
import React from 'react';
import { getRGBAColor } from '../functions/getRGBAColor';
import { darkPalette, lightPalette } from './palettes';

declare module '@mui/material/styles' {
  interface Components {
    MuiDataGrid?: {
      styleOverrides?: {
        root?: React.CSSProperties;
        row?: React.CSSProperties;
        panel?: React.CSSProperties;
      };
    };
  }
}

export const lightTheme = createTheme({
  palette: lightPalette,
  typography: {
    fontFamily: 'Onest, sans-serif',
    allVariants: { color: '#16151C' },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiDayCalendar-weekDayLabel': {
            color: lightPalette.text.primary,
          },
        },
        h6: {
          fontWeight: '400',
        },
        h4: {
          fontWeight: '600',
        },
        subtitle1: {
          fontSize: '14px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: lightPalette.text.gray,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.MuiPickersDay': {
            backgroundColor: `var(--primaryColor)`,
          },
          '&.MuiLoadingButton': {
            fontSize: '14px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          fontWeight: '300',
          borderRadius: 10,
          padding: '10px 15px',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: lightPalette.background.paper,
          color: lightPalette.text.primary,
        },
        containedPrimary: {
          backgroundColor: `var(--primaryColor)`,
          color: '#ffffff',
        },
        containedError: {
          backgroundColor: lightPalette.error.main,
          color: '#ffffff',
        },
        containedSuccess: {
          backgroundColor: lightPalette.success.main,
          color: '#ffffff',
        },
        contained: {
          backgroundColor: lightPalette.background.paper,
        },
        containedSecondary: {
          backgroundColor: lightPalette.background.light,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: lightPalette.primary.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `var(--primaryColor)`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: lightPalette.error.main,
          },
          '.MuiSelect-outlined': {
            display: 'flex',
            flexWrap: 'wrap',
            whiteSpace: 'normal !important',
          },
          '.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
        notchedOutline: {
          borderColor: lightPalette.primary.light,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: lightPalette.error.main,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .Mui-checked + .MuiSwitch-track': {
            backgroundColor: `${lightPalette.success.main} !important`,
          },
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          paddingTop: '0 !important',
          overflow: 'visible !important',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '.MuiStack': {
            paddingTop: 0,
            overflow: 'visible',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          padding: '15px',
          borderRadius: '0 10px 10px 0 ',
          fontSize: '16px',
          cursor: 'pointer',
          textDecoration: 'none',
          borderLeft: '3px solid transparent',
          color: lightPalette.text.primary,
          '&.active': {
            backgroundColor: getRGBAColor('--primaryColor', 0.08),
            borderLeftColor: `var(--primaryColor)`,
            color: `var(--primaryColor)`,
            fontWeight: '600',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: lightPalette.background.default,
          boxShadow: 'unset',
          paddingBottom: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'unset',
          border: `1px solid ${lightPalette.primary.light}`,
          opacity: 1,
          backgroundColor: lightPalette.background.default,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'unset',
          backgroundImage: 'unset',
          backgroundColor: 'transparent',
          border: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          margin: '10px',
          '&.border-left': {
            borderLeft: `4px solid ${lightPalette.primary.light}`,
            '&.active': {
              borderColor: `var(--primaryColor)`,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          borderBottom: `1px solid ${lightPalette.primary.light}`,
          '&.MuiTableCell-head': {
            padding: '16px',
            color: lightPalette.text.gray,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '20px',
          boxShadow: 'unset',
          border: `1px solid ${lightPalette.primary.light}`,
          backgroundColor: 'transparent',
          borderRadius: '10px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: '4px',
          height: '24px',
          '&.MuiChip-colorError': {
            backgroundColor: darkPalette.error.light,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: darkPalette.warning.light,
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: darkPalette.success.light,
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: lighten(lightPalette.primary.main, 0.9),
            color: darkPalette.primary.main,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // backdropFilter: 'blur(5px)',
          transition: '0.5s ease-in-out',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '.MuiModalBody': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: lightPalette.background.default,
            borderRadius: '20px',
            padding: '2rem',
            outline: 'none',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          flexDirection: 'row',
          textTransform: 'capitalize',
          color: lightPalette.text.primary,
          '.MuiTab-icon': {
            marginRight: '6px',
          },
          '&.Mui-selected': {
            fontWeight: '500',
          },
          '&[error="true"]': {
            borderBottom: `1px solid ${darkPalette.error.main}`,
            color: lightPalette.error.main,
          },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaderTitle': {
            color: lightPalette.text.gray,
            fontWeight: '400',
          },
        } as React.CSSProperties,
        panel: {
          backgroundColor: lightPalette.primary.light,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          overFlowClipMargin: 'border-box',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: `var(--primaryColor)`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: darkPalette,
  typography: {
    fontFamily: 'Onest, sans-serif',
  },

  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: darkPalette.text.primary,
          '&.MuiDayCalendar-weekDayLabel': {
            color: darkPalette.text.primary,
          },
        },
        h6: {
          fontWeight: '400',
        },
        h5: {
          fontWeight: '400',
        },
        h4: {
          fontWeight: '600',
        },
        subtitle1: {
          fontSize: '14px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: darkPalette.background.light,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          fontWeight: '300',
          borderRadius: 10,
          padding: '10px 15px',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        startIcon: {
          '&.file-upload': {
            margin: 0,
          },
        },
        outlined: {
          borderColor: darkPalette.info.main,
          backgroundColor: darkPalette.background.default,
          color: darkPalette.text.primary,
        },
        containedPrimary: {
          backgroundColor: `var(--primaryColor)`,
          color: darkPalette.text.primary,
        },
        containedError: {
          backgroundColor: darkPalette.error.main,
          color: '#ffffff',
        },
        containedSuccess: {
          backgroundColor: darkPalette.success.main,
          color: '#ffffff',
        },
        contained: {
          backgroundColor: darkPalette.background.light,
          color: darkPalette.text.primary,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: darkPalette.background.light,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: darkPalette.text.primary,
          '&.Mui-disabled': {
            color: darkPalette.secondary.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: darkPalette.background.light,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: darkPalette.background.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `var(--primaryColor)`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: darkPalette.error.main,
          },
          '.MuiSelect-outlined': {
            display: 'flex',
            flexWrap: 'wrap',
            whiteSpace: 'normal !important',
          },
          '.MuiSelect-icon': {
            color: darkPalette.background.light,
          },
          '.Mui-disabled': {
            cursor: 'not-allowed',
            '-webkit-text-fill-color': `${darkPalette.secondary.main} !important`,
          },
        },
        notchedOutline: {
          borderColor: darkPalette.background.light,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: darkPalette.error.main,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '.MuiSwitch-track': {
            backgroundColor: `${darkPalette.background.light} !important`,
          },
          '& .Mui-checked + .MuiSwitch-track': {
            backgroundColor: `${lightPalette.success.main} !important`,
          },
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          paddingTop: '0 !important',
          overflow: 'visible !important',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          padding: '15px',
          borderRadius: '0 10px 10px 0 ',
          fontSize: '16px',
          cursor: 'pointer',
          textDecoration: 'none',
          borderLeft: '3px solid transparent',
          '&.active': {
            backgroundColor: getRGBAColor('--primaryColor', 0.08),
            borderLeftColor: `var(--primaryColor)`,
            color: `var(--primaryColor)`,
            fontWeight: '600',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: darkPalette.background.default,
          boxShadow: 'unset',
          paddingBottom: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'unset',
          border: `1px solid ${darkPalette.background.light}`,
          opacity: 1,
          backgroundColor: darkPalette.background.default,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'unset',
          backgroundImage: 'unset',
          backgroundColor: 'transparent',
          border: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          margin: '10px',
          '&.border-left': {
            borderLeft: `4px solid ${darkPalette.background.light}`,
            '&.active': {
              borderColor: `var(--primaryColor)`,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          borderBottom: `1px solid ${darkPalette.background.light}`,
          '&.MuiTableCell-head': {
            padding: '16px',
            color: darkPalette.info.main,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '20px',
          boxShadow: 'unset',
          border: `1px solid ${darkPalette.background.light}`,
          backgroundColor: 'transparent',
          borderRadius: '10px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: '4px',
          height: '24px',
          '&.MuiChip-colorError': {
            backgroundColor: darkPalette.error.dark,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: darkPalette.warning.dark,
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: darkPalette.success.dark,
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: darken(darkPalette.primary.main, 0.7),
            color: darkPalette.primary.main,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // backdropFilter: 'blur(5px)',
          transition: '0.5s ease-in-out',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '.MuiModalBody': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: darkPalette.background.default,
            borderRadius: '20px',
            padding: '2rem',
            outline: 'none',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          flexDirection: 'row',
          textTransform: 'capitalize',
          color: darkPalette.text.primary,
          '.MuiTab-icon': {
            marginRight: '6px',
          },
          '&.Mui-selected': {
            fontWeight: '500',
          },
          '&[error="true"]': {
            borderBottom: `1px solid ${darkPalette.error.main}`,
            color: darkPalette.error.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        scroller: {
          marginBottom: '0px !important',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaderTitle': {
            color: darkPalette.text.gray,
            fontWeight: '400',
          },
          color: darkPalette.secondary.main,
          '--rowBorderColor': darkPalette.background.light,
          '--DataGrid-rowBorderColor': darkPalette.background.light,
          borderColor: darkPalette.background.light,
          '.MuiDataGrid-withBorderColor': {
            borderColor: darkPalette.background.light,
          },
          '& .MuiSvgIcon-root': {
            fill: darkPalette.secondary.main,
          },
        } as React.CSSProperties,
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: darkPalette.secondary.main,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: `var(--primaryColor)`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: darkPalette.background.light,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          borderColor: darkPalette.background.light,
          backgroundColor: darkPalette.background.default,
          '.MuiAlert-message': {
            color: darkPalette.secondary.main,
          },
        },
      },
    },
  },
});
export const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  () => ({
    '&.notistack-MuiContent-success': {
      backgroundColor: lightPalette.success.main,
    },
    '&.notistack-MuiContent-error': {
      backgroundColor: lightPalette.error.main,
    },
    '&.notistack-MuiContent-warning': {
      backgroundColor: lightPalette.warning.main,
    },
    '&.notistack-MuiContent-info': {
      backgroundColor: lightPalette.info.main,
    },
  }),
);
